import React, { useRef, useLayoutEffect, useEffect } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import { useValueForCurrentScreenSize } from '@volkswagen-onehub/components-core/dist/helpers/hooks/use-value-for-current-screen-size';
import {
	ControlContainer,
	FullscreenButton,
	PlayButton,
	PlayButtonCenter,
	TimeDisplay,
	Timeline,
	Video as VideoComponent,
	VolumeSlider,
} from '@volkswagen-onehub/components-core/dist/video';

gsap.registerPlugin(ScrollTrigger);
const INITIAL_FRAME_SECONDS = 2;
const VIDEO_START_WIDTH = '10vw';
const VIDEO_END_WIDTH = '70vw';
const VIDEO_END_WIDTH_MOBILE = '80vw';
const VIDEO_END_TOP = 'var(--size-dynamuic0200)';
const DESKTOP_VIDEO_URL =
	'https://assets.volkswagen.com/is/content/volkswagenag/VW_MASTERS%20DE%20LA%20CARRETERA_16_9-0x720-3000k';
const MOBILE_VIDEO_URL =
	'https://assets.volkswagen.com/is/content/volkswagenag/VW_MASTERS%20DE%20LA%20CARRETERA_9_16-0x720-3000k';

const VideoSection = styled.section<{ $videoEndWidth: string }>`
	position: relative;
	width: 100%;
	padding-top: var(--size-dynamic0250);
	box-sizing: border-box;
	overflow: hidden;
	background-color: #ffffff;

	&::after {
		content: '';
		display: block;
		width: ${({ $videoEndWidth }) => $videoEndWidth};
		aspect-ratio: 16 / 9;
		margin: 0 auto;
		visibility: hidden;
		pointer-events: none;
	}

	@media (max-width: 559px) {
		&::after {
			aspect-ratio: 9 / 16;
		}
	}
`;

const FinalVideoContainer = styled.div`
	position: absolute;
	top: ${VIDEO_END_TOP};
	left: 50%;
	width: ${VIDEO_START_WIDTH};
	height: auto;
	aspect-ratio: 16 / 9;
	opacity: 1;
	border-radius: 16px;
	overflow: hidden;

	& > div {
		width: 100%;
		height: 100%;
	}

	video {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: 16px;
	}

	@media (max-width: 559px) {
		aspect-ratio: 9 / 16;
	}
`;

export default function Video() {
	const container = useRef<HTMLElement | null>(null);
	const finalVideoRef = useRef<HTMLDivElement | null>(null);
	const hasStartedPlaybackRef = useRef(false);
	const scrollTriggerRef = useRef<ScrollTrigger | null>(null);

	const isMin560 = useValueForCurrentScreenSize<boolean>({
		[Breakpoints.default]: false,
		[Breakpoints.b560]: true,
	});
	const videoEndWidth = isMin560 ? VIDEO_END_WIDTH : VIDEO_END_WIDTH_MOBILE;
	const videoSrc = isMin560 ? DESKTOP_VIDEO_URL : MOBILE_VIDEO_URL;

	const syncScrollAnimation = () => {
		const scrollTrigger = scrollTriggerRef.current;
		if (!scrollTrigger) {
			return;
		}

		scrollTrigger.refresh();
		scrollTrigger.update();

		const totalDistance = scrollTrigger.end - scrollTrigger.start;
		if (totalDistance <= 0) {
			return;
		}

		const currentScroll = scrollTrigger.scroll();
		const rawProgress = (currentScroll - scrollTrigger.start) / totalDistance;
		const clampedProgress = Math.max(0, Math.min(1, rawProgress));
		scrollTrigger.animation?.progress(clampedProgress);
	};

	useEffect(() => {
		// Cuando cambia el breakpoint, actualizamos el vídeo.
		const videoElement = finalVideoRef.current?.querySelector('video');
		hasStartedPlaybackRef.current = false;

		if (!videoElement) {
			return;
		}

		videoElement.pause();
		videoElement.src = videoSrc;
		videoElement.load();

		// Tras cambiar source, resincronizamos la animación con el scroll real.
		videoElement.addEventListener('loadeddata', syncScrollAnimation);
		videoElement.addEventListener('loadedmetadata', syncScrollAnimation);
		const fallbackSyncTimeout = window.setTimeout(syncScrollAnimation, 250);

		return () => {
			window.clearTimeout(fallbackSyncTimeout);
			videoElement.removeEventListener('loadeddata', syncScrollAnimation);
			videoElement.removeEventListener('loadedmetadata', syncScrollAnimation);
		};
	}, [videoSrc]);

	useLayoutEffect(() => {
		let cleanupPreviewFrameListener: (() => void) | undefined;

		// Animación de scroll
		const ctx = gsap.context(() => {
			const introSection = document.querySelector<HTMLElement>('[data-testid="intro-section"]');
			const videoContainer = finalVideoRef.current;
			const videoElement = videoContainer?.querySelector('video');

			if (!introSection || !videoContainer || !videoElement) {
				return;
			}

			// Deja el vídeo en un frame inicial (preview) antes de reproducir.
			const setPreviewFrame = () => {
				if (hasStartedPlaybackRef.current || !Number.isFinite(videoElement.duration)) {
					return;
				}

				const lastSeekableTime = Math.max(videoElement.duration - 0.1, 0);
				videoElement.currentTime = Math.min(INITIAL_FRAME_SECONDS, lastSeekableTime);
				videoElement.pause();
			};

			if (videoElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
				setPreviewFrame();
			} else {
				videoElement.addEventListener('loadedmetadata', setPreviewFrame);
				cleanupPreviewFrameListener = () => {
					videoElement.removeEventListener('loadedmetadata', setPreviewFrame);
				};
			}

			const tl = gsap.timeline({
				scrollTrigger: {
					id: 'video-reveal-pin',
					trigger: introSection,
					start: 'bottom bottom',
					end: 'bottom top',
					scrub: 1,
					invalidateOnRefresh: true,
					onUpdate: (self) => {
						if (hasStartedPlaybackRef.current || self.progress < 1) {
							return;
						}

						// Reproduce desde el inicio solo una vez.
						videoElement.currentTime = 0;
						videoElement
							.play()
							.then(() => {
								hasStartedPlaybackRef.current = true;
							})
							.catch(() => {
								// Ignore autoplay rejections; user interaction can still start playback.
							});
					},
				},
			});

			tl.fromTo(
				videoContainer,
				{
					xPercent: -50,
					yPercent: 100,
				},
				{
					width: videoEndWidth,
					xPercent: -50,
					yPercent: 0,
					ease: 'power2.out',
				},
			);

			scrollTriggerRef.current = tl.scrollTrigger ?? null;
			syncScrollAnimation();
		}, container);

		window.addEventListener('resize', syncScrollAnimation);

		return () => {
			window.removeEventListener('resize', syncScrollAnimation);
			scrollTriggerRef.current = null;
			cleanupPreviewFrameListener?.();
			ctx.revert();
		};
	}, [videoEndWidth]);

	return (
		<VideoSection ref={container} $videoEndWidth={videoEndWidth}>
			<FinalVideoContainer ref={finalVideoRef}>
				<VideoComponent src={videoSrc} muted playsInline>
					<PlayButtonCenter playTitle="Play video" />
					<ControlContainer hideInitially>
						<PlayButton playTitle="Play video" pauseTitle="Pause video" />
						<Timeline />
						<TimeDisplay />
						<VolumeSlider muteTitle="Mute video" unmuteTitle="Unmute video" />
						<FullscreenButton
							openTitle="Open video in fullscreen"
							closeTitle="Close fullscreen"
						/>
					</ControlContainer>
				</VideoComponent>
			</FinalVideoContainer>
		</VideoSection>
	);
}
